<template>
  <AppContainer>
    <div>
      <EPageTitleMobile :title="t(useTranslationPath(callerPath, 'header'))">
        <span>{{ t(useTranslationPath(callerPath, 'description')) }}</span>
        <br />
        <span v-html="phoneNumber" />
      </EPageTitleMobile>
      <EInput
        id="search"
        v-model="searchInput"
        icon="magnifying-glass"
        name="search"
        :placeholder="t(useTranslationPath(callerPath, 'searchWorkOrders'))"
        class="mb-2"
      />
      <div v-if="isLoading" class="text-center">
        <ESpinner size="2xl" />
      </div>
      <div v-else>
        <EList>
          <EListItem
            v-for="workOrder in workOrders"
            :id="workOrder.id"
            :key="workOrder.id"
            :title="workOrder.summary"
            :description="workOrder.primary_location_path"
            :status="workOrder.state_name"
            :status-variant="workOrder.statusVariant"
            @click="selectWorkOrder(workOrder)"
          >
            <EListItemProperty
              v-if="workOrder.due_date"
              :label="t(useTranslationPath(callerPath, 'dueBy'))"
              :value="useDayJs(workOrder.due_date).format('L')"
            />
          </EListItem>
        </EList>

        <EPagination
          v-model="pageOffset"
          v-model:per-page="pageSize"
          :total-items="totalWorkOrders"
        />
      </div>
    </div>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import { useGetConfigVar } from '@/composables/useGetConfigVar.js'
import { useTranslationPath } from '@/composables/useTranslationPath'
import { useAppStore } from '@/stores/AppStore'
import { useEngineerStore } from '@/stores/authenticated/EngineerStore.js'
import { useWorkOrderStore } from '@/stores/authenticated/WorkOrderStore'
import { useDayJs } from '@expansivefm/api-request-parser'
import {
  EInput,
  EList,
  EListItem,
  EListItemProperty,
  EPageTitleMobile,
  EPagination,
  ESpinner,
} from '@expansivefm/eds'
import { useDebounceFn } from '@vueuse/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const router = useRouter()

const { t } = useI18n()

const appStore = useAppStore()
const engineerStore = useEngineerStore()
const workOrderStore = useWorkOrderStore()

const callerPath = 'views.authenticated.workOrders.index'

const searchInput = ref('')
const workOrders = ref([])

const pageOffset = ref(1)
const pageSize = ref(10)
const totalWorkOrders = ref(0)

const isLoading = ref(false)

const phoneNumber = computed(() => {
  const phone = useGetConfigVar('ENGINEER_APP_HELPDESK_NUMBER')

  if (phone) {
    return `<a href='tel:${phone.replace(' ', '')}'>${t(useTranslationPath(callerPath, 'callUsOn'), { phoneNumber: phone })}</a>`
  }

  return t(useTranslationPath(callerPath, 'notAvailable'))
})

watch(searchInput, async (newValue) => {
  if (newValue !== '') {
    await searchWorkOrders()
  } else {
    await fetchWorkOrders()
  }
})

watch(
  () => pageOffset.value,
  async (page) => {
    await fetchWorkOrders()
  },
  {
    immediate: false,
  },
)

watch(
  () => pageSize.value,
  async (size) => {
    pageOffset.value = 1

    await fetchWorkOrders()
  },
  {
    immediate: false,
  },
)

onMounted(async () => {
  workOrderStore.$patch({ workOrder: {} })
  appStore.$patch({ showBackButton: false })

  await engineerStore
    .sessions(['activeEngineers', 'engineers', 'workOrderStartWorkCheck'], { status: 'OPEN' })
    .then(async ({ data }) => {
      if (data.length > 0) {
        engineerStore.$patch({ previousActiveSessions: data })

        await router.push({
          name: 'sessions',
        })
      } else {
        await fetchWorkOrders()
      }
    })
})

const searchWorkOrders = useDebounceFn(async () => {
  if (searchInput.value) {
    isLoading.value = true

    pageOffset.value = 1

    let filters = {}

    const isNumericString = (s) => /^\d+$/.test(s.trim())

    if (isNumericString(searchInput.value)) {
      filters = { id: searchInput.value }
    } else {
      filters = { summary: searchInput.value }
    }

    await workOrderStore
      .fetchWorkOrders(filters, pageOffset.value, pageSize.value)
      .then(({ data, meta: { page } }) => {
        isLoading.value = false

        totalWorkOrders.value = page.total

        parseWorkOrders(data)
      })
  }
}, 500)

const fetchWorkOrders = async () => {
  isLoading.value = true

  const filters = searchInput.value !== '' ? { summary: searchInput.value } : {}

  await workOrderStore
    .fetchWorkOrders(filters, pageOffset.value, pageSize.value)
    .then(({ data, meta: { page } }) => {
      isLoading.value = false

      totalWorkOrders.value = page.total

      parseWorkOrders(data)
    })
}

const parseWorkOrders = (data) => {
  workOrders.value = data.map((workOrder) => ({
    ...workOrder,
    // TODO: Add a function to determine the status variant based on work order state
    statusVariant: workOrder.state_status === 'completed' ? 'success' : 'warning',
  }))
}

const selectWorkOrder = (workOrder) => {
  workOrderStore.$patch({ workOrder })

  router.push({
    name: 'work-order.engineers.index',
    params: { workOrderId: workOrder.id },
  })
}
</script>
